import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import FeatureFlagsService from '@/api/featureFlags';

export const useFeatureFlagsStore = defineStore('featureFlags', {
  state: () => ({
    flags: {} as Record<string, boolean>,
    isLoading: false,
    isPartnerScoped: false,
    fetchPromise: null as Promise<void> | null,
  }),
  getters: {
    isEnabled: state => {
      return (key: string): boolean => state.flags[key] ?? false;
    },
    flagKeys: state => Object.keys(state.flags),
  },
  actions: {
    // the country-level fetch (main.ts) and the partner-scoped one
    // (PartnersApp.vue) can otherwise overlap and the second, authoritative
    // call gets silently dropped
    async fetchFlags(partnerUserId?: string) {
      if (this.fetchPromise) {
        await this.fetchPromise;
      }

      this.isLoading = true;
      this.fetchPromise = (async () => {
        try {
          this.flags = partnerUserId
            ? await FeatureFlagsService.getPartnerFlags(partnerUserId)
            : await FeatureFlagsService.getCountryFlags();
          if (partnerUserId) {
            this.isPartnerScoped = true;
          }
        } catch (error) {
          this.flags = {} as Record<string, boolean>;
          Sentry.captureException(error, {
            level: 'warning',
            fingerprint: ['feature-flags-fetch-failed'],
            tags: { partnerScoped: String(!!partnerUserId) },
          });
        } finally {
          this.isLoading = false;
        }
      })();

      try {
        await this.fetchPromise;
      } finally {
        this.fetchPromise = null;
      }
    },
  },
});
